import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { QK } from "../../../base/qk";
import {
  getRunsByArea,
  getWeeklyAssignmentByVisit,
  updateWeeklyAssignment,
} from "../../../api/services/scheduling";
import moment from "moment";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Stack,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { Add, CheckCircle, Person, AccessTime, CalendarToday, AddCircleOutline } from "@mui/icons-material";
import AddEmployee from "../../ClientsScreen/AddEmployee";
import { getClientArea } from "../../../api/services/Client";
import CustomSelect from "../../../components/FormFields/CustomSelect/CustomSelect";
import { useSnackbar } from "notistack";
import CreateRunModal from "../../ClientsScreen/CreateRunModal";
import { formatTimeRangeToAmPm } from "../../../base/FormatTime";

const EditEmployee = ({ visitId, clientId, activeKey, area }) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [changedRuns, setChangedRuns] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const handleOpenModal = (id, run) => {
    if (!run) {
      enqueueSnackbar("Add a run first", { variant: "warning" });
      return;
    }
    setJobId(id);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const { data: weeklyAssignment, isLoading: isLoadingAssignment } = useQuery(
    [QK.weeklyAssignment, visitId],
    () => getWeeklyAssignmentByVisit(visitId),
    { enabled: !!visitId }
  );

  const { data: runsByArea, isLoading: isLoadingRuns } = useQuery(
    [QK.runsByArea, area],
    () => getRunsByArea(area),
    { enabled: !!area }
  );

  const runOptions = runsByArea?.map((r) => ({
    value: r.id,
    label: r.display_name || r.name || `Run ${r.id}`,
  })) || [];

  const handleRunChange = (assignmentId, newRun) => {
    setChangedRuns((prev) => ({
      ...prev,
      [assignmentId]: newRun,
    }));
  };

  const handleSubmitRun = async (assignmentId) => {
    const run = changedRuns[assignmentId];
    const response = await updateWeeklyAssignment({ run }, assignmentId);
    if (response.status === 200) {
      enqueueSnackbar("Run added successfully", { variant: "success" });
      queryClient.invalidateQueries([QK.weeklyAssignment]);
      queryClient.invalidateQueries([QK.visitPlan, QK.runsByArea]);
    } else {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  };

  const getDayColor = (day) => {
    const colors = {
      Monday: "#3B82F6",
      Tuesday: "#8B5CF6",
      Wednesday: "#EC4899",
      Thursday: "#F59E0B",
      Friday: "#10B981",
      Saturday: "#EF4444",
      Sunday: "#6B7280",
    };
    return colors[day] || "#6B7280";
  };

  const headers = [
    { key: "weekly_day", label: "Week Day" },
    { key: "time", label: "Time" },
    { key: "carer", label: "Carer" },
    { key: "run", label: "Run" },
  ];

  const renderCell = (item, header) => {
    const key = typeof header === "object" ? header.key : header;

    if (key === "weekly_day") {
      return (
        <Chip
          label={item.weekly_day}
          size="small"
          sx={{
            bgcolor: alpha(getDayColor(item.weekly_day), 0.12),
            color: getDayColor(item.weekly_day),
            fontWeight: 600,
            height: 24,
            fontSize: "0.7rem",
          }}
        />
      );
    }

    if (key === "time") {
      return (
        <Typography variant="body2" fontWeight={500} color="#1A1A2E">
          {formatTimeRangeToAmPm(item.time_required_from, item.time_required_to)}
        </Typography>
      );
    }

    if (key === "carer") {
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {item.employee_details?.map((emp, index) => (
            <Chip
              key={index}
              label={emp.first_name}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                height: 24,
                fontSize: "0.65rem",
                fontWeight: 500,
              }}
            />
          ))}
          {(!item.employee_details || item.employee_details.length === 0) && (
            <Typography variant="caption" color="#94A3B8">
              No employees
            </Typography>
          )}
        </Stack>
      );
    }

    if (key === "run") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomSelect
            options={runOptions}
            value={changedRuns[item.id] || item.run || ""}
            onChange={(e) => handleRunChange(item.id, e.target.value)}
            placeholder="Select Run"
            size="small"
            fullWidth
            minWidth="140px"
          />
          {changedRuns[item.id] && changedRuns[item.id] !== item.run && (
            <Tooltip title="Save Run" arrow>
              <IconButton
                size="small"
                onClick={() => handleSubmitRun(item.id)}
                sx={{
                  color: "#22C55E",
                  "&:hover": {
                    bgcolor: alpha("#22C55E", 0.08),
                  },
                }}
              >
                <CheckCircle sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Create New Run" arrow>
            <IconButton
              size="small"
              onClick={() => setShowRunModal(true)}
              sx={{
                color: "#94A3B8",
                "&:hover": {
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <AddCircleOutline sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return item[key] || "-";
  };

  if (isLoadingAssignment || isLoadingRuns) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Table size="medium">
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    color: "#475467",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {header.key === "weekly_day" && <CalendarToday sx={{ fontSize: 14, color: "#94A3B8" }} />}
                    {header.key === "time" && <AccessTime sx={{ fontSize: 14, color: "#94A3B8" }} />}
                    {header.key === "carer" && <Person sx={{ fontSize: 14, color: "#94A3B8" }} />}
                    {header.key === "run" && <AddCircleOutline sx={{ fontSize: 14, color: "#94A3B8" }} />}
                    <span>{header.label}</span>
                  </Stack>
                </TableCell>
              ))}
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475467",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  textAlign: "center",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {weeklyAssignment && weeklyAssignment.length > 0 ? (
              weeklyAssignment.map((item, index) => (
                <TableRow
                  key={item.id || index}
                  hover
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": { bgcolor: alpha("#2469F0", 0.02) },
                  }}
                >
                  {headers.map((header) => {
                    const key = typeof header === "object" ? header.key : header;
                    return (
                      <TableCell
                        key={key}
                        sx={{
                          color: "#1A1A2E",
                          fontSize: "0.78rem",
                          py: 0.75,
                          px: 1.5,
                          borderBottom: "1px solid #F2F4F7",
                        }}
                      >
                        {renderCell(item, header)}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      borderBottom: "1px solid #F2F4F7",
                      textAlign: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 0.25, justifyContent: "center" }}>
                      <Tooltip title="Add Employee" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(item.id, item.run)}
                          sx={{
                            color: "#667085",
                            p: 0.5,
                            "&:hover": {
                              color: "#2469F0",
                              bgcolor: alpha("#2469F0", 0.06),
                            },
                          }}
                        >
                          <Person sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={headers.length + 1}
                  sx={{
                    textAlign: "center",
                    py: 3,
                    color: "#98A2B3",
                    fontSize: "0.85rem",
                  }}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddEmployee
        show={showModal}
        handleClose={handleCloseModal}
        area={area}
        jobId={jobId}
      />
      <CreateRunModal
        show={showRunModal}
        handleClose={() => setShowRunModal(false)}
        area={area}
      />
    </Box>
  );
};

export default EditEmployee;